import React, { useState } from "react";
import TextFieldWithValidation from "./TextFieldWithValidation";
import { Box, TextField, Typography } from "@mui/material";
import zipcodes from "zipcodes";

const ActorForm = ({ formData, handleInputChange, handleBlur, errors }) => {
  const [location, setLocation] = useState(null);
  const [fNameError, setFNameError] = React.useState({
    state: false,
    message: "",
  });
  const [lNameError, setLNameError] = React.useState({
    state: false,
    message: "",
  });
  const [emailError, setEmailError] = React.useState({
    state: false,
    message: "",
  });
  const [phoneError, setPhoneError] = React.useState({
    state: false,
    message: "",
  });
  const [zipError, setZipError] = React.useState({
    state: false,
    message: "",
  });
  const [passwordError, setPasswordError] = React.useState({
    state: false,
    message: "",
  });
  const [confirmPasswordError, setConfirmPasswordError] = React.useState({
    state: false,
    message: "",
  });

  const validateName = (name) => {
    return name.length > 0;
  };

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    const re = /^\d{10}$/;
    return re.test(phoneNumber);
  };

  const validateZipCode = (zipCode) => {
    const re = /^\d{5}$/;
    return re.test(zipCode);
  };

  const validatePassword = (password) => {
    return (
      password.length >= 8 &&
      password.length <= 20 &&
      /\d/.test(password) &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password)
    );
  };

  const validateConfirmPassword = (confirmPassword) => {
    return confirmPassword === formData.password;
  };

  const handleZipCodeChange = (e) => {
    const { value } = e.target;
    handleInputChange(e);

    // Look up the location based on the zip code
    const locationInfo = zipcodes.lookup(value);
    if (locationInfo) {
      const location = `${locationInfo.city}, ${locationInfo.state}`;
      setLocation(location);

      handleInputChange({
        target: {
          name: "location",
          value: location,
        },
      });
    } else {
      setLocation(null);

      handleInputChange({
        target: {
          name: "location",
          value: "",
        },
      });
    }
  };

  const validateForm = () => {
    let valid = true;

    if (!validateName(formData.firstName)) {
      setFNameError({ state: true, message: "First name is required" });
      valid = false;
    } else {
      setFNameError({ state: false, message: "" });
    }

    if (!validateName(formData.lastName)) {
      setLNameError({ state: true, message: "Last name is required" });
      valid = false;
    } else {
      setLNameError({ state: false, message: "" });
    }

    if (!validateEmail(formData.email)) {
      setEmailError({ state: true, message: "Invalid email" });
      valid = false;
    } else {
      setEmailError({ state: false, message: "" });
    }

    if (!validatePhoneNumber(formData.phoneNumber)) {
      setPhoneError({ state: true, message: "Invalid phone number" });
      valid = false;
    }

    if (!validateZipCode(formData.zipCode)) {
      setZipError({ state: true, message: "Invalid zip code" });
      valid = false;
    }

    if (!validatePassword(formData.password)) {
      setPasswordError({
        state: true,
        message:
          "Password must be 8-20 characters long and contain at least one number, one lowercase letter, and one uppercase letter",
      });
      valid = false;
    }

    if (!validateConfirmPassword(formData.confirmPassword)) {
      setConfirmPasswordError({
        state: true,
        message: "Passwords do not match",
      });
      valid = false;
    }

    return valid;
  };

  return (
    <>
      <Box className="name-fields">
        <TextFieldWithValidation
          name="firstName"
          label="First Name"
          value={formData.firstName}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={errors.firstName}
        />
        <TextFieldWithValidation
          name="lastName"
          label="Last Name"
          value={formData.lastName}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={errors.lastName}
        />
      </Box>
      <Box className="name-fields">
        <TextFieldWithValidation
          name="zipCode"
          label="Zip Code"
          value={formData.zipCode}
          onChange={handleZipCodeChange}
          onBlur={handleBlur}
          error={errors.zipCode}
        />
        {location && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Location: {location}
          </Typography>
        )}
        <TextFieldWithValidation
          name="phoneNumber"
          label="Phone Number"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={errors.phoneNumber}
        />
      </Box>
      <TextFieldWithValidation
        name="email"
        label="Email"
        value={formData.email}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={errors.email}
      />
      <Box className="name-fields">
        <TextFieldWithValidation
          name="birthday"
          label="Birthday"
          type="date"
          value={formData.birthday}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={errors.birthday}
        />
        <TextFieldWithValidation
          sx={{
            width: "170px",
            "@media (max-width: 760px)": {
              width: "100%",
            },
          }}
          name="gender"
          label="Gender"
          value={formData.gender}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={errors.gender}
          select
          options={[
            { value: "male", label: "Male" },
            { value: "female", label: "Female" },
            { value: "trans", label: "Trans" },
            { value: "other", label: "Other" },
          ]}
        />
      </Box>
      <TextFieldWithValidation
        name="ethnicity"
        label="Ethnicity"
        value={formData.ethnicity}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={errors.ethnicity}
        select
        options={[
          { value: "American African", label: "American African" },
          // { value: "white", label: "white" },
          { value: "American European", label: "American European" },
          { value: "Latino Hispanic", label: "Latino Hispanic" },
          { value: "asian", label: "Asian" },
          { value: "middle_eastern", label: "Middle Eastern" },
          { value: "native_american", label: "Native American" },
          { value: "pacific_islander", label: "Pacific Islander" },
          { value: "indian south_asian", label: "Indian South Asian" },
          { value: "other", label: "Other" },
        ]}
      />
      <TextFieldWithValidation
        name="password"
        label="Create Password"
        type="password"
        value={formData.password}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={errors.password}
      />
      <TextFieldWithValidation
        name="confirmPassword"
        label="Confirm Password"
        type="password"
        value={formData.confirmPassword}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={errors.confirmPassword}
      />
    </>
  );
};

export default ActorForm;
